import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import MarketButtonMobile from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/market-button/market-button-mobile';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import {
  Link,
  Typography,
  performLogout,
} from '@loomstateorg/shared-frontend';
import Button from 'components/commercetools-ui/Button';
import { Category } from 'types/category';
import { useAccount } from 'frontastic';
import MobileMenuNavButton from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/header/header-navigation/header-navigation-mobile/components/menu-nav-button';

export interface Props {
  showMenu: boolean;
  insertCategory: (category: Category) => void;
  hideHeaderMenu: () => void;
  children?: React.ReactNode;
  accountSubCategories: Category[];
}

const MobileMenuFooter: FC<Props> = ({ showMenu, hideHeaderMenu, insertCategory, accountSubCategories }) => {
  const router = useRouter();
  const { formatMessage } = useFormat({ name: 'common' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const marketButtonRef = useRef<HTMLDivElement>(null);
  // Removed session variable - using direct performLogout function
  const { account, logout, getCustomerGroup, isOrderApprover } = useAccount();
  const [languageMenuTop, setLanguageMenuTop] = useState(false);
  const [allowedReports, setAllowedReports] = useState([]);
  const [isApprover, setIsApprover] = useState(false);

  const shouldShowDashboard = allowedReports?.length > 0;

  const accountButton: Category = useMemo(
    () => ({
      categoryId: 'myAccount',
      linkName: formatAccountMessage({ id: 'my.account', defaultMessage: 'My Account' }),
      depth: 1,
      subCategories: accountSubCategories,
    }),
    [accountSubCategories, formatAccountMessage],
  );

  const handleLogout = async () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });

      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => caches.delete(cacheName));
      });
    }
    hideHeaderMenu();
    // Use direct performLogout function to avoid SessionProvider dependency
    await performLogout(null, logout);
  };

  const getAllowedReports = useCallback(async () => {
    const customerGroup = await getCustomerGroup();

    if (customerGroup) {
      const allowedReports = customerGroup?.customerGroup?.obj?.custom?.fields?.allowedReports || [];
      setAllowedReports(allowedReports);
    }
  }, [getCustomerGroup]);

  const checkIsOrderApprover = useCallback(async () => {
    setIsApprover(await isOrderApprover());
  }, [isOrderApprover]);

  useEffect(() => {
    if (account) {
      getAllowedReports();
      checkIsOrderApprover();
    }
  }, [account, getAllowedReports, checkIsOrderApprover]);

  useLayoutEffect(() => {
    if (marketButtonRef.current) {
      if (window.innerHeight - marketButtonRef.current.getBoundingClientRect().bottom < 100) setLanguageMenuTop(true);
      else setLanguageMenuTop(false);
    }
  }, [showMenu]);

  return (
    <>
      <div className="mb-16 mt-12 w-full border border-neutral-400" />

      {account ? (
        <div className="ml-24 mr-22 block pt-16 md:hidden" data-testid="mobile-menu-account">
          <MobileMenuNavButton
            link={accountButton}
            onClick={() => insertCategory(accountButton)}
            hideHeaderMenu={hideHeaderMenu}
          />
        </div>
      ) : (
        <div className="ml-24 mr-22 block py-16 md:hidden">
          <Link link="/help" onClick={hideHeaderMenu} className="py-16" data-testid="help">
            <Typography fontSize={14} className="text-primary-regular">
              {formatMessage({ id: 'help.and.support', defaultMessage: 'Help & Customer Service' })}
            </Typography>
          </Link>{' '}
        </div>
      )}

      <div className="mx-24 flex py-16" data-testid="mobile-menu-footer">
        {account ? (
          <Button variant="ghost" size="fit" onClick={handleLogout} data-testid="sign-out">
            <Typography className="text-16 font-normal text-primary-regular lg:text-18">
              {formatAccountMessage({ id: 'sign.out', defaultMessage: 'Sign out' })}
            </Typography>
          </Button>
        ) : (
          <Link link="/login" onClick={hideHeaderMenu} className="px-0" data-testid="sign-in">
            <Typography fontSize={14} className="font-normal text-primary-regular">
              {formatAccountMessage({ id: 'sign.in', defaultMessage: 'Sign in' })}
            </Typography>
          </Link>
        )}
      </div>
    </>
  );
};

export default MobileMenuFooter;
